/**
 * TEMPORARY VIDEO INFERENCE API SERVICE
 *
 * This file contains API calls for video inference demo functionality.
 * It uses a separate endpoint (port 8000) and is not integrated with the main API.
 *
 * TODO: Remove this file when video inference is fully integrated into the main API.
 */

// Base URL for video inference (different from main API)
const VIDEO_API_BASE_URL = process.env.REACT_APP_VIDEO_API_ENDPOINT || 'http://localhost:8000'

// Response types
export interface ProcessVideoResponse {
  message: string
  job_id: string
  video_path: string
}

export interface StreamStatusResponse {
  status: string
  [key: string]: any // Allow for additional status fields
}

/**
 * Upload a video file for inference processing in the background
 *
 * @param file - Video file to process
 * @returns Promise with job information
 */
export const processVideoBackground = async (file: File): Promise<ProcessVideoResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${VIDEO_API_BASE_URL}/process_video`, {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to process video: ${response.status} ${errorText}`)
  }

  return response.json()
}

// Response type for create_stream endpoint
export interface CreateStreamResponse {
  message: string
  stream_id: string
  stream_url: string
  full_url: string
  frame_skip: number
}

/**
 * Create a stream session by uploading a video file
 *
 * @param file - Video file to stream
 * @param frameSkip - Number of frames to skip between inferences (default: 1)
 * @returns Promise with stream session information
 */
export const createStreamSession = async (
  file: File,
  frameSkip = 1
): Promise<CreateStreamResponse> => {
  console.log('Creating stream session:', {
    endpoint: VIDEO_API_BASE_URL,
    file: file.name,
    fileSize: file.size,
    frameSkip,
  })

  const formData = new FormData()
  formData.append('file', file)
  formData.append('frame_skip', frameSkip.toString())

  let response
  try {
    response = await fetch(`${VIDEO_API_BASE_URL}/create_stream`, {
      method: 'POST',
      body: formData,
    })
  } catch (error: any) {
    console.error('Network error connecting to video API:', error)
    const errorDetails = error?.message || error?.toString() || 'Unknown network error'
    throw new Error(
      `Cannot connect to video inference service at ${VIDEO_API_BASE_URL}. ` +
        'Please ensure the service is running and CORS is enabled for POST /create_stream. ' +
        `Details: ${errorDetails}`
    )
  }

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Create stream error response:', { status: response.status, error: errorText })
    throw new Error(`Failed to create stream: ${response.status} ${errorText}`)
  }

  const data = await response.json()
  console.log('Stream session created successfully:', data)

  return data
}

/**
 * Upload video for streaming inference and return job details
 * This uses the /stream_video endpoint which uploads and streams back the result
 *
 * @param file - Video file to process
 * @param frameSkip - Number of frames to skip between inferences
 * @returns Promise with job ID and streaming URL for real-time playback
 */
export const uploadForStreaming = async (
  file: File,
  frameSkip = 1
): Promise<{ jobId: string; streamUrl: string }> => {
  console.log('Uploading video for real-time streaming inference:', {
    endpoint: VIDEO_API_BASE_URL,
    file: file.name,
    fileSize: file.size,
    frameSkip,
  })

  // Create a unique request ID for this upload
  const requestId = `stream_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  // Build the streaming URL with the file
  const formData = new FormData()
  formData.append('file', file)

  const streamUrl = `${VIDEO_API_BASE_URL}/stream_video?frame_skip=${frameSkip}&request_id=${requestId}`

  console.log('Video streaming URL ready:', streamUrl)

  return { jobId: requestId, streamUrl }
}

/**
 * Get the current status of the stream service
 *
 * @returns Promise with stream status information
 */
export const getStreamStatus = async (): Promise<StreamStatusResponse> => {
  const response = await fetch(`${VIDEO_API_BASE_URL}/stream/status`, {
    method: 'GET',
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to get stream status: ${response.status} ${errorText}`)
  }

  return response.json()
}

/**
 * Process a live video stream (RTSP, HTTP, etc.) with inference
 *
 * @param inputUrl - URL of the input stream (optional, uses config default if not provided)
 * @param frameSkip - Number of frames to skip between inferences (optional, uses config default if not provided)
 * @param onProgress - Optional callback for streaming progress
 * @returns Promise with the video blob
 */
export const processLiveStream = async (
  inputUrl?: string,
  frameSkip?: number,
  onProgress?: (loaded: number, total: number) => void
): Promise<Blob> => {
  const params = new URLSearchParams()
  if (inputUrl) params.append('input_url', inputUrl)
  if (frameSkip !== undefined) params.append('frame_skip', frameSkip.toString())

  const url = `${VIDEO_API_BASE_URL}/stream${params.toString() ? `?${params.toString()}` : ''}`

  const response = await fetch(url, {
    method: 'GET',
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to process live stream: ${response.status} ${errorText}`)
  }

  // Read the response as a blob with progress tracking
  const contentLength = response.headers.get('content-length')
  const total = contentLength ? parseInt(contentLength, 10) : 0

  if (!response.body) {
    throw new Error('Response body is null')
  }

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let loaded = 0

  while (true) {
    const { done, value } = await reader.read()

    if (done) break

    chunks.push(value)
    loaded += value.length

    if (onProgress && total) {
      onProgress(loaded, total)
    }
  }

  // Combine chunks into a single blob
  return new Blob(chunks, { type: 'video/mp2t' })
}

/**
 * Helper function to convert video blob to object URL for playback
 *
 * @param blob - Video blob
 * @returns Object URL string
 */
export const createVideoObjectURL = (blob: Blob): string => {
  return URL.createObjectURL(blob)
}

/**
 * Helper function to revoke object URL to free memory
 *
 * @param url - Object URL to revoke
 */
export const revokeVideoObjectURL = (url: string): void => {
  URL.revokeObjectURL(url)
}

/**
 * Test connection to video inference service
 *
 * @returns Promise with connection status
 */
export const testConnection = async (): Promise<{ connected: boolean; message: string }> => {
  try {
    const response = await fetch(`${VIDEO_API_BASE_URL}/stream/status`, {
      method: 'GET',
    })

    if (response.ok) {
      const data = await response.json()
      return { connected: true, message: 'Connected successfully', ...data }
    } else {
      return { connected: false, message: `Service returned status ${response.status}` }
    }
  } catch (error: any) {
    console.error('Connection test failed:', error)
    return {
      connected: false,
      message: `Cannot reach ${VIDEO_API_BASE_URL}. Is the service running?`,
    }
  }
}
